import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ActivityRow from '../components/ActivityRow'
import { useNavigationGuard } from '../context/NavigationContext'
import { usePageData } from '../hooks/usePageData'
import { useLang } from '../context/LangContext'
import { apiUrl, apiFetch } from '../utils/shopifyFetch'
import { syncSourceInfo } from '../lib/syncSource'
import { useOrdersHidden } from '../hooks/useOrdersHidden'

/**
 * How long ago, in the words a person would use.
 *
 * Shown against a figure that is only as fresh as the last scan. A stale
 * number presented as current is worse than one that admits its age.
 */
function fmtWhen(iso) {
  if (!iso) return 'never'
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return 'never'
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (mins < 1)    return 'just now'
  if (mins < 60)   return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24)    return `${hrs} hour${hrs === 1 ? '' : 's'} ago`
  const days = Math.round(hrs / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

function fmtCurrency(n) {
  const v = parseFloat(n || 0)
  return v >= 1000 ? `$${(v/1000).toFixed(1)}k` : `$${v.toFixed(2)}`
}
function fmtDate(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-US', { month:'short', day:'numeric' })
}
function initials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()
}
const AVATAR_COLORS = [
  { bg:'#e3f5ee', color:'#00a47c' },
  { bg:'#e8f0fe', color:'#005bd3' },
  { bg:'#fff0e8', color:'#c05000' },
  { bg:'#f2e8ff', color:'#6b00d4' },
  { bg:'#fff5e6', color:'#b05e00' },
]
function avatarColor(name) {
  const code = name ? name.charCodeAt(0) : 0
  return AVATAR_COLORS[code % AVATAR_COLORS.length]
}

export default function Dashboard({ shop, addToast, justOnboarded = false }) {
  const navigate      = useNavigate()
  // Declared with the other hooks, above the loading and empty returns: a hook
  // placed after an early return runs on some renders and not others.
  const [feedTab, setFeedTab] = useState('all')
  const { authorize } = useNavigationGuard()
  const { t }         = useLang()
  // Up here for the reason stated directly above, which is not a style note:
  // this sat below the loading and empty returns, so it did not run on the
  // first render and did run on the second. React counts hooks per render and
  // threw on the change, which the error boundary caught — the whole dashboard
  // became "Something went wrong" the moment its data arrived.
  //
  // It reads a settings URL and does not depend on the dashboard's own data,
  // so there was never a reason for it to be down there.
  const { hidden: ordersHidden } = useOrdersHidden(shop)

  function go(path) { authorize(); navigate(path) }

  const { data: dash, loading, invalidate } = usePageData(
    shop ? apiUrl('/dashboard', shop) : null,
    { skip: !shop }
  )

  /**
   * The first import, started here rather than waited for.
   *
   * Onboarding used to end on a dashboard with nothing on it and no indication
   * that anything was coming — the seller had finished setting the app up and it
   * looked broken. This brings the catalogue in on arrival, once, and only
   * straight after onboarding: every later visit leaves it alone, because a
   * dashboard that re-imports on every visit is a dashboard that fights the
   * seller's own edits.
   */
  const started = useRef(false)
  const hub = dash?.stats?.syncSource
  // Null for a shop whose hub is unknown, so the badge is absent rather than
  // guessing — and never "undefined → undefined".
  const sourceInfo = syncSourceInfo(hub)
  const ebayHubNeedsBootstrap = !!hub && hub !== 'shopify'
    && dash?.stats?.ebaySourceBootstrapped === false
  // The Shopify-hub mirror of the line above, and deliberately shaped the
  // same way: hub is exactly 'shopify', and the server has said the first
  // import has NOT run. `=== false` rather than falsy, so a payload that
  // predates the field does nothing instead of importing on every load.
  const shopifyHubNeedsImport = hub === 'shopify'
    && dash?.stats?.shopifySourceBootstrapped === false

  useEffect(() => {
    if (!shop || started.current || !hub) return

    // With eBay as the hub this runs whether or not they have just onboarded,
    // because it is the step that stops duplicates and a seller who closed the
    // tab half way through setup would otherwise never get it.
    if (ebayHubNeedsBootstrap) {
      started.current = true
      addToast?.('success', 'Reading your Shopify catalogue so eBay listings can be matched to it')
      apiFetch('POST', '/products/ebay-source/bootstrap', shop, {})
        .then(d => {
          if (d?.skipped) return
          const bits = []
          if (d.imported) bits.push(`imported ${d.imported} Shopify product${d.imported === 1 ? '' : 's'}`)
          if (d.linked)   bits.push(`linked ${d.linked} eBay listing${d.linked === 1 ? '' : 's'}`)
          addToast?.('success', bits.length ? bits.join(', ') : 'Nothing needed matching')
          const left = (d.ambiguous || 0) + (d.unmatched || 0)
          if (left) {
            addToast?.('warning',
              `${left} eBay listing${left === 1 ? '' : 's'} could not be matched — review them under Products`)
          }
          invalidate()
        })
        .catch(e => addToast?.('error', `Could not match your eBay listings: ${e.message}`))
      return
    }

    // Shopify as the hub, and the catalogue has never been read.
    //
    // Runs whether or not they have just onboarded, for the same reason the
    // eBay branch above does — and because the handler that was supposed to do
    // this is never reached. Onboarding's last step is the plan; finishing it
    // leaves for Shopify's charge approval; the return lands on
    // ?subscribed=1, a branch that marks the app ready and returns. The
    // import and the link pass behind it never happen. Measured on dev:
    // POST /products/shopify-source/bootstrap had been called zero times ever,
    // while link/auto — its sibling in that same handler — showed 140 calls
    // from elsewhere in the app.
    //
    // So a seller chose Shopify as their hub, landed here, and the app stayed
    // empty until they found the Import button themselves.
    //
    // Safe to run on arrival: the route stamps shopify_source_bootstrap_at and
    // answers alreadyDone afterwards, so this fires once per shop however many
    // times the dashboard is opened, and the flag above turns it off as soon
    // as the server reports it done.
    if (shopifyHubNeedsImport) {
      started.current = true
      addToast?.('success', 'Bringing your Shopify products in — this can take a minute')
      apiFetch('POST', '/products/shopify-source/bootstrap', shop, {})
        .then(d => {
          // Said out loud, including when it declines. The old callers
          // discarded this — one with `.catch(() => {})` and a note that the
          // scheduled pass imports anyway — and a 200 carrying
          // { skipped: 'not_shopify_source' } is success-shaped and silent,
          // which is why nobody could tell the call was never happening.
          if (d?.skipped) {
            addToast?.('warning', `Products were not imported: ${d.skipped}`)
          } else if (d?.imported) {
            addToast?.('success', `Imported ${d.imported} product${d.imported === 1 ? '' : 's'} from Shopify`)
          } else if (!d?.alreadyDone) {
            addToast?.('success', 'Your Shopify catalogue is already up to date here')
          }
          if (d?.error) addToast?.('warning', d.error)
          invalidate()
        })
        .catch(e => addToast?.('error', `Could not import your products: ${e.message}`))
      return
    }

    if (!justOnboarded) return
    started.current = true
    addToast?.('success', 'Bringing your Shopify products in — this can take a minute')
    apiFetch('POST', '/products/import', shop, {})
      .then(d => {
        if (d?.imported) {
          addToast?.('success', `Imported ${d.imported} product${d.imported === 1 ? '' : 's'} from Shopify`)
        }
        invalidate()
      })
      .catch(e => addToast?.('error', `Could not import your products: ${e.message}`))
  }, [justOnboarded, shop, hub, ebayHubNeedsBootstrap, shopifyHubNeedsImport])   // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="fade-in" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300 }}>
      <div style={{ textAlign:'center', color:'var(--text3)' }}>
        <div style={{ fontSize:28, animation:'spin 1s linear infinite', display:'inline-block' }}>↺</div>
        <div style={{ marginTop:8, fontSize:13 }}>{t('loading')}</div>
      </div>
    </div>
  )

  if (!dash?.stats) return (
    <div className="fade-in">
      <div className="card" style={{ textAlign:'center', padding:48 }}>
        <div style={{ fontSize:36, marginBottom:12, opacity:.4 }}>⊞</div>
        <div style={{ fontSize:16, fontWeight:650, marginBottom:8 }}>{t('no_data_yet')}</div>
        <div style={{ fontSize:13, color:'var(--text3)', marginBottom:20 }}>
          {!shop ? 'Install the app from Shopify to get started.' : t('import_products')}
        </div>
        <button className="btn btn-primary" onClick={() => go('/products')}>{t('go_products')}</button>
      </div>
    </div>
  )

  const { stats, recentActivity, recentOrders } = dash

  // One place, one solution: /upload holds the products with no rule, the rule
  // itself, and the upload, and moves between them on its own. Sending an
  // established seller to Products instead only looked helpful — it dropped
  // them back into the four-screen version of the same job.
  const uploadPath = '/upload'
  /**
   * eBay is the hub, so the direction of the work is the other way round.
   *
   * The wording follows it. "Upload your products to eBay" is the Shopify hub's
   * job; here the products come from eBay and what is missing is the Shopify
   * side of them, so both the step and the button say so and point at the
   * screen that does it. Only the words and the destination change — the
   * Shopify hub reads exactly as it did.
   */
  // two_way reads as eBay-sourced here as well: the listings are where the
  // seller works, so the figures and the wording follow eBay. Only the Shopify
  // hub keeps the original.
  const ebayHub = hub !== 'shopify' && !!hub
  const listLabel = ebayHub ? 'List products on Shopify from eBay' : 'List products on eBay'
  // Straight onto the section that does the work, not the product list — which
  // under an eBay hub is empty at exactly the moment this button matters.
  const listPath  = ebayHub ? '/products?tab=unlinked-ebay' : uploadPath

  // Whether this page reports on orders at all.
  //
  // NOT `ebayHub` above, deliberately. That is `hub !== 'shopify' && !!hub`,
  // which is true for two_way as well — right for the catalogue figures, where
  // two_way does behave like the eBay hub, and wrong here: it would have taken
  // the Orders section away from every two-way seller.
  //
  // Through the hook rather than off `hub` above, because the answer also
  // depends on which Shopify app the shop installed from: the custom-app shop
  // keeps the section whatever its hub, and /dashboard does not report that.
  // The hook reads the settings URL both navs already populate, so it costs no
  // extra request.
  //
  // An unknown shop shows the section, which is the direction the rest of this
  // file errs in too — a figure that appears a moment late beats one that
  // disappears in front of the person reading it.
  //
  // The hook itself is called at the top of the component, with the others.
  const showOrdersSection = !ordersHidden

  // The whole path from a fresh install to a live listing, in the order it has
  // to happen: each step is blocked until the one above it is done. The old
  // list skipped connecting eBay and creating a rule, which are exactly the two
  // steps a new seller gets stuck on — and counted publishing twice to reach
  // three.
  const gettingStartedSteps = [
    {
      done: !!stats.ebayConnected,
      label: 'Connect your eBay account',
      sub: 'Authorise SyncE Bridge to list and sync on your behalf',
      action: () => go('/settings'),
      cta: 'Connect eBay',
    },
    {
      done: stats.totalProducts > 0,
      // Under an eBay hub this step is not the seller importing a catalogue to
      // sell — it is the app reading what they already have, so eBay listings
      // can be matched to it instead of duplicated. Saying "import your
      // products" there describes the wrong job.
      label: ebayHub ? 'Read your Shopify catalogue' : 'Import your Shopify products',
      sub: ebayHub ? 'So eBay listings can be matched to products you already have'
                   : 'Bring your catalogue into SyncE Bridge',
      action: () => go('/products'),
      cta: ebayHub ? 'Read catalogue' : 'Import Products',
    },
    // A listing rule decides what a product is published to eBay under. With
    // eBay as the hub nothing is published from here, so the step is not one
    // the seller has to finish — it is dropped from the list rather than left
    // sitting there permanently unticked.
    ...(ebayHub ? [] : [{
      done: stats.totalProfiles > 0,
      label: 'Create a listing rule',
      sub: 'Decides the eBay category, price, postage and returns for your products',
      action: () => go(uploadPath),
      cta: 'Upload products on eBay',
    }]),
    {
      done: stats.activeListings > 0,
      label: ebayHub ? 'List your eBay products on Shopify' : 'Upload your products to eBay',
      sub: ebayHub ? 'Give each eBay listing a product in your Shopify store'
                   : 'Put them live and start selling',
      action: () => go(listPath),
      cta: ebayHub ? listLabel : 'Upload Now',
    },
  ]

  const doneCount = gettingStartedSteps.filter(s => s.done).length
  const allDone   = doneCount === gettingStartedSteps.length
  // The first thing still outstanding — what the Upload button should do, so it
  // never drops a seller somewhere they cannot act yet.
  const nextStep  = gettingStartedSteps.find(s => !s.done)

  const shopName = shop ? shop.replace('.myshopify.com','') : 'your store'

  // Money, on the right. Three figures, each one a way into Orders — so with
  // Orders hidden they are three cards that lead nowhere, and they report on
  // the section being hidden. Empty for an eBay-hub shop; the definition
  // stays below it, which is all that has to come back.
  const moneyCards = !showOrdersSection ? [] : [
    { label: t('total_revenue'), value: fmtCurrency(stats.totalRevenue), ink:'var(--green)',  rule:'var(--green)',
      sub: `from ${stats.syncedOrders ?? 0} synced order${stats.syncedOrders === 1 ? '' : 's'}` },
    { label: t('last_30_days'),  value: fmtCurrency(stats.revenue30d),   ink:'var(--accent)', rule:'var(--accent)',
      sub: `${stats.orders30d ?? 0} order${stats.orders30d === 1 ? '' : 's'} this month` },
    { label: t('total_orders'),  value: String(stats.totalOrders ?? 0),  ink:'var(--text)',   rule:'var(--border2)',
      sub: stats.failedOrders > 0 ? `${stats.failedOrders} failed to sync` : 'all synced' },
  ]

  // The catalogue as one bar rather than five counts: how much is live, how much
  // is drafted, how much has never been listed. Guarded against an empty
  // catalogue, where every share would be NaN.
  const productCount = Math.max(0, stats.totalProducts ?? 0)
  const livePct  = productCount ? Math.round(((stats.activeListings ?? 0) / productCount) * 100) : 0
  const draftPct = productCount
    ? Math.max(0, Math.round((((stats.totalListings ?? 0) - (stats.activeListings ?? 0)) / productCount) * 100))
    : 0

  // The same bar, measured from the other end. Under an eBay hub the whole is
  // the eBay catalogue and the question is how much of it has a Shopify
  // product behind it — not how many Shopify products reached eBay, which is
  // what the figures above answer.
  const listingCount   = Math.max(0, stats.totalListings ?? 0)
  const inShopify      = Math.max(0, stats.listingsInShopify ?? 0)
  // Null until something has scanned eBay. Not the same as zero: a listing
  // waiting for a product has no row here, so it can only be counted by asking
  // eBay, and a dashboard cannot do that on every load. Whatever the Create
  // products from eBay screen last found is what this reports.
  const awaitingShopify = stats.listingsAwaitingShopify
  const awaitingKnown   = awaitingShopify != null
  const inShopifyPct   = listingCount ? Math.round((inShopify / listingCount) * 100) : 0

  // Orders and sync in one feed. Two panels side by side each showed half as
  // much, and the orders appeared in both.
  const FEED_TABS = [
    { id: 'all',    label: 'Everything' },
    { id: 'orders', label: t('recent_orders') },
    { id: 'sync',   label: 'Sync' },
  // The orders tab goes with the section, and without it the other two show
  // exactly the same feed — so the row is dropped rather than left as two
  // buttons with no difference between them. feedTab stays 'all', which is
  // what the sync feed below reads.
  ].filter(() => showOrdersSection)
  const showOrders = showOrdersSection && feedTab !== 'sync' && !!recentOrders?.length
  const showSync   = feedTab !== 'orders' && !!recentActivity?.length
  const feedEmpty  = !showOrders && !showSync

  return (
    <div className="fade-in">

      {/* A plain header rather than a gradient panel.
          The blue wash with a waving hand and two emoji buttons was the single
          loudest thing on the page and said the least: the greeting changes
          nothing, and the gradient is the giveaway of a template. The shop's
          name, its connection and the one action worth taking are enough. */}
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <h1 style={{ fontSize:21, fontWeight:650, letterSpacing:'-0.01em', margin:0 }}>{shopName}</h1>
            {/* The hub, chosen once at onboarding and never shown again.
                Everything on this page already branches on it — which way the
                progress bar counts, what the subtitle reports, what the main
                button does — so the seller was reading a page arranged around
                an answer they could not see. Beside the shop name because it
                is a property of the shop, not a statistic. */}
            {sourceInfo && (
              <span title={sourceInfo.sub}
                    style={{ fontSize:11, fontWeight:600, letterSpacing:'.02em',
                             padding:'3px 9px', borderRadius:999,
                             background:'var(--accent-bg)', color:'var(--accent)',
                             border:'1px solid var(--border)', whiteSpace:'nowrap' }}>
                {sourceInfo.label}
              </span>
            )}
          </div>
          <div style={{ fontSize:13, color:'var(--text3)', marginTop:2 }}>
            {stats.ebayConnected
              ? (ebayHub
                  // What is outstanding, not what is live: under this hub the
                  // listings are already live by definition — the seller did
                  // that on eBay — and the open question is how many of them
                  // still have no product in Shopify.
                  ? (awaitingKnown
                      ? (awaitingShopify
                          ? `Connected to eBay · ${awaitingShopify} listing${awaitingShopify!==1?'s':''} still to create in Shopify`
                          : 'Connected to eBay · every listing has a Shopify product')
                      : 'Connected to eBay · check which listings still need a Shopify product')
                  : `Connected to eBay · ${stats.activeListings} listing${stats.activeListings!==1?'s':''} live`)
              : 'Not connected to eBay yet'}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', marginLeft:'auto', flexWrap:'wrap' }}>
          <button className="btn" onClick={() => go('/products')}>Products</button>
          <button className="btn btn-primary" onClick={() => go(listPath)}>
            {allDone ? listLabel : (nextStep?.cta ?? listLabel)}
          </button>
        </div>
      </div>

      {/* ── SKU alert strip ──
          Hidden with the section: it counts orders and its only action is to
          open Orders. The orders still fail to fulfil and the backend still
          records why — this is the notice, not the condition. */}
      {showOrdersSection && stats.skuErrorOrders > 0 && (
        <button
          onClick={() => go('/orders')}
          aria-label={`${stats.skuErrorOrders} orders need a SKU fix — go to Orders`}
          style={{ width:'100%', cursor:'pointer', display:'flex', alignItems:'center', gap:10, background:'var(--amber-bg)', border:'1px solid var(--amber)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, fontFamily:'inherit', textAlign:'left' }}>
          <span><strong>{stats.skuErrorOrders} order{stats.skuErrorOrders!==1?'s':''}</strong> need a SKU fix before they can be fulfilled on Shopify.</span>
          <span style={{ marginLeft:'auto', color:'var(--amber)', fontWeight:650, textDecoration:'underline' }}>Fix now →</span>
        </button>
      )}

      {/* Tasks on the left, information on the right.
          The rail holds everything with something to do — setup, whatever needs
          attention, and where the catalogue stands. The right side holds the
          money and one merged feed. Two columns instead of five stacked
          sections is what makes the page fit a screen without scrolling. */}
      <div className="dash-cols" style={{ display:'grid', gridTemplateColumns:'332px minmax(0, 1fr)', gap:14, alignItems:'start' }}>

        {/* ── Left rail: the things to do ── */}
        <div style={{ display:'flex', flexDirection:'column', gap:12, minWidth:0 }}>

          {/* Gone for good once setup is finished */}
          {!allDone && (
            <div className="card" style={{ border:'1px solid var(--accent)', background:'var(--accent-bg)' }}>
              <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:9 }}>
                <div style={{ fontSize:14, fontWeight:650 }}>Finish setup</div>
                <div style={{ marginLeft:'auto', fontSize:12, color:'var(--text3)', fontVariantNumeric:'tabular-nums' }}>
                  {doneCount} / {gettingStartedSteps.length}
                </div>
              </div>
              <div style={{ height:4, background:'var(--border)', borderRadius:4, marginBottom:10 }}>
                <div style={{ height:4, background:'var(--accent)', borderRadius:4, width:`${(doneCount/gettingStartedSteps.length)*100}%`, transition:'width .4s' }} />
              </div>
              {gettingStartedSteps.map((step, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0' }}>
                  <span aria-hidden="true" style={{ width:18, height:18, borderRadius:'50%', flexShrink:0, fontSize:10, fontWeight:650, display:'flex', alignItems:'center', justifyContent:'center',
                                 border:`1.5px solid ${step.done ? 'var(--green)' : 'var(--accent)'}`,
                                 background: step.done ? 'var(--green)' : 'transparent',
                                 color: step.done ? '#fff' : 'var(--accent)' }}>
                    {step.done ? '✓' : i + 1}
                  </span>
                  <span style={{ flex:1, fontSize:13,
                                 color: step.done ? 'var(--text3)' : 'var(--text)',
                                 textDecoration: step.done ? 'line-through' : 'none' }}>
                    {step.label}{step.done ? ' — done' : ''}
                  </span>
                </div>
              ))}
              {nextStep && (
                <button onClick={nextStep.action} className="btn btn-primary btn-sm" style={{ width:'100%', marginTop:10 }}>
                  {nextStep.cta}
                </button>
              )}
            </div>
          )}

          {/* Only when something is actually wrong — and only while Orders is
              shown, since both rows count orders and both open that page. */}
          {showOrdersSection && (stats.skuErrorOrders > 0 || stats.failedOrders > 0) && (
            <div className="card" style={{ border:'1px solid var(--amber)', background:'var(--amber-bg)' }}>
              <div className="stat-label" style={{ color:'var(--amber)' }}>Need attention</div>
              {stats.skuErrorOrders > 0 && (
                <button onClick={() => go('/orders')}
                  aria-label={`${stats.skuErrorOrders} orders need a SKU fix — go to Orders`}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 0', background:'none', border:'none', borderBottom: stats.failedOrders > 0 ? '1px solid var(--border)' : 'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                  <span style={{ fontSize:17, fontWeight:650, color:'var(--amber)', fontVariantNumeric:'tabular-nums', minWidth:22 }}>{stats.skuErrorOrders}</span>
                  <span style={{ flex:1, fontSize:13 }}>order{stats.skuErrorOrders!==1?'s':''} need a SKU fix</span>
                  <span aria-hidden="true" style={{ color:'var(--amber)' }}>›</span>
                </button>
              )}
              {stats.failedOrders > 0 && (
                <button onClick={() => go('/orders')}
                  aria-label={`${stats.failedOrders} orders failed to sync — go to Orders`}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 0', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                  <span style={{ fontSize:17, fontWeight:650, color:'var(--red)', fontVariantNumeric:'tabular-nums', minWidth:22 }}>{stats.failedOrders}</span>
                  <span style={{ flex:1, fontSize:13 }}>order{stats.failedOrders!==1?'s':''} failed to sync</span>
                  <span aria-hidden="true" style={{ color:'var(--red)' }}>›</span>
                </button>
              )}
            </div>
          )}

          {/* Where the catalogue stands, as one picture instead of five counts */}
          <div className="card">
            <div className="stat-label">{ebayHub ? 'eBay catalogue' : 'Catalogue'}</div>
            {ebayHub ? (<>
              {/* Counted from eBay, because eBay is what the seller has. The
                  Shopify-hub figures below ask how many products reached eBay,
                  which here is the wrong way round and would read as a
                  catalogue mostly unpublished. */}
              <button onClick={() => go('/products?tab=unlinked-ebay')}
                aria-label={`${listingCount} eBay listings — go to Products`}
                style={{ width:'100%', display:'flex', alignItems:'baseline', gap:9, marginTop:9, background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                <span style={{ fontSize:25, fontWeight:650, letterSpacing:'-0.02em', fontVariantNumeric:'tabular-nums' }}>{listingCount}</span>
                <span style={{ fontSize:13, color:'var(--text3)' }}>eBay listings</span>
              </button>
              <button onClick={() => go('/products?tab=unlinked-ebay')}
                aria-label={`${inShopify} have a Shopify product — go to Products`}
                style={{ width:'100%', display:'flex', alignItems:'baseline', gap:9, marginTop:9, background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                <span style={{ fontSize:25, fontWeight:650, letterSpacing:'-0.02em', color:'var(--green)', fontVariantNumeric:'tabular-nums' }}>{inShopify}</span>
                <span style={{ fontSize:13, color:'var(--text3)' }}>
                  in Shopify{awaitingKnown && awaitingShopify ? `, ${awaitingShopify} still to create` : ''}
                </span>
              </button>
              <div style={{ height:6, background:'var(--bg4)', borderRadius:6, marginTop:14, overflow:'hidden', display:'flex' }}>
                <div style={{ width:`${inShopifyPct}%`, background:'var(--green)' }} />
              </div>
              <div style={{ display:'flex', gap:14, marginTop:9, fontSize:11, color:'var(--text3)', flexWrap:'wrap' }}>
                <span style={{ display:'flex', alignItems:'center', gap:5 }}><span aria-hidden="true" style={{ width:7, height:7, borderRadius:2, background:'var(--green)' }} />In Shopify</span>
                <span style={{ display:'flex', alignItems:'center', gap:5 }}><span aria-hidden="true" style={{ width:7, height:7, borderRadius:2, background:'var(--bg4)' }} />Still to create</span>
              </div>
              {/* Said out loud, because the figure is only as fresh as the last
                  scan and a stale number presented as current is worse than one
                  that admits its age. */}
              <div style={{ fontSize:11, color:'var(--text3)', marginTop:6 }}>
                {awaitingKnown
                  ? `Last checked against eBay ${fmtWhen(stats.listingsAwaitingCheckedAt)}`
                  : 'Not checked against eBay yet — open Create products from eBay'}
              </div>
            </>) : (<>
            <button onClick={() => go('/products')}
              aria-label={`${stats.totalProducts ?? 0} products in the app — go to Products`}
              style={{ width:'100%', display:'flex', alignItems:'baseline', gap:9, marginTop:9, background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
              <span style={{ fontSize:25, fontWeight:650, letterSpacing:'-0.02em', fontVariantNumeric:'tabular-nums' }}>{stats.totalProducts ?? 0}</span>
              <span style={{ fontSize:13, color:'var(--text3)' }}>products in the app</span>
            </button>
            <button onClick={() => go('/products')}
              aria-label={`${stats.activeListings ?? 0} live on eBay — go to Products`}
              style={{ width:'100%', display:'flex', alignItems:'baseline', gap:9, marginTop:9, background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
              <span style={{ fontSize:25, fontWeight:650, letterSpacing:'-0.02em', color:'var(--green)', fontVariantNumeric:'tabular-nums' }}>{stats.activeListings ?? 0}</span>
              <span style={{ fontSize:13, color:'var(--text3)' }}>live on eBay, of {stats.totalListings ?? 0}</span>
            </button>
            <div style={{ height:6, background:'var(--bg4)', borderRadius:6, marginTop:14, overflow:'hidden', display:'flex' }}>
              <div style={{ width:`${livePct}%`, background:'var(--green)' }} />
              <div style={{ width:`${draftPct}%`, background:'var(--accent)', opacity:.35 }} />
            </div>
            <div style={{ display:'flex', gap:14, marginTop:9, fontSize:11, color:'var(--text3)', flexWrap:'wrap' }}>
              <span style={{ display:'flex', alignItems:'center', gap:5 }}><span aria-hidden="true" style={{ width:7, height:7, borderRadius:2, background:'var(--green)' }} />Live</span>
              <span style={{ display:'flex', alignItems:'center', gap:5 }}><span aria-hidden="true" style={{ width:7, height:7, borderRadius:2, background:'var(--accent)', opacity:.35 }} />Draft</span>
              <span style={{ display:'flex', alignItems:'center', gap:5 }}><span aria-hidden="true" style={{ width:7, height:7, borderRadius:2, background:'var(--bg4)' }} />Not listed</span>
            </div>
            </>)}
            <button onClick={() => go('/settings')}
              aria-label={stats.ebayConnected ? 'eBay connected — go to Settings' : 'eBay not connected — go to Settings'}
              style={{ width:'100%', display:'flex', alignItems:'center', gap:8, marginTop:14, paddingTop:12, borderTop:'1px solid var(--border)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
              <span aria-hidden="true" style={{ width:7, height:7, borderRadius:'50%', flexShrink:0, background: stats.ebayConnected ? 'var(--green)' : 'var(--red)' }} />
              <span style={{ flex:1, fontSize:12, color:'var(--text3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {stats.ebayConnected ? (stats.ebayShopName || t('connected')) : t('not_connected')}
              </span>
              <span aria-hidden="true" style={{ color:'var(--text3)' }}>›</span>
            </button>
          </div>

          {/* Book a call.
              In the left column, under the catalogue, because that column is
              short and this sits above the fold. It was first put below the
              activity feed, where it was only reachable by scrolling past
              everything — an offer of help nobody sees is not an offer.

              Only when one is configured: the server sends null unless
              CALENDLY_URL is set and is a safe https URL, and an empty
              booking link is worse than none.

              target="_blank" because the app is an iframe inside Shopify
              admin. Navigating this frame to an external page is the bug that
              broke Subscribe, and a scheduling page refuses to be framed the
              same way. rel="noopener noreferrer" so the new tab cannot reach
              back through window.opener. */}
          {dash.bookingUrl && (
            <div className="card">
              <div className="card-title">Need a hand?</div>
              <div style={{ fontSize:12.5, color:'var(--text2)', lineHeight:1.5, marginBottom:12 }}>
                Book a short call and we will walk through your sync, your listings
                or anything that is not behaving as you expect.
              </div>
              <a
                className="btn btn-sm btn-primary"
                href={dash.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display:'inline-block', textDecoration:'none' }}
              >
                Schedule a meeting →
              </a>
            </div>
          )}
        </div>

        {/* ── Right: the money, then one feed ── */}
        <div style={{ display:'flex', flexDirection:'column', gap:12, minWidth:0 }}>

          <div className="dash-money" style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0, 1fr))', gap:12 }}>
            {moneyCards.map(c => (
              <button key={c.label} onClick={() => go('/orders')} className="card"
                aria-label={`${c.label}: ${c.value} — go to Orders`}
                style={{ cursor:'pointer', textAlign:'left', fontFamily:'inherit', borderTop:`3px solid ${c.rule}`, transition:'box-shadow .2s' }}
                onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.08)'}
                onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                <div className="stat-label">{c.label}</div>
                <div style={{ fontSize:28, fontWeight:650, letterSpacing:'-0.02em', marginTop:6, color:c.ink, fontVariantNumeric:'tabular-nums' }}>{c.value}</div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>{c.sub}</div>
              </button>
            ))}
          </div>

          {/* One feed. Two panels side by side each showed half as much, and the
              orders appeared in both. */}
          <div className="card" style={{ minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, flexWrap:'wrap' }}>
              <div className="card-title" style={{ marginBottom:0 }}>What just happened</div>
              <div style={{ marginLeft:'auto', display:'flex', gap:4 }}>
                {FEED_TABS.map(tab => (
                  <button key={tab.id} onClick={() => setFeedTab(tab.id)}
                    aria-pressed={feedTab === tab.id}
                    className="btn btn-sm"
                    style={{ fontSize:12,
                             borderColor: feedTab === tab.id ? 'var(--accent)' : 'var(--border)',
                             background:  feedTab === tab.id ? 'var(--accent-bg)' : 'transparent',
                             color:       feedTab === tab.id ? 'var(--accent)' : 'var(--text3)',
                             fontWeight:  feedTab === tab.id ? 650 : 500 }}>
                    {tab.label}
                  </button>
                ))}
              </div>
              <button onClick={() => go(showOrdersSection && feedTab === 'orders' ? '/orders' : '/activity')}
                style={{ fontSize:12, fontWeight:550, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', padding:'3px 8px', borderRadius:4 }}>
                {t('view_all')} →
              </button>
            </div>

            {showOrders && recentOrders.map((o, i) => {
              const av = avatarColor(o.buyer_name)
              return (
                <div key={`o-${i}`} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                  <div aria-hidden="true" style={{ width:30, height:30, borderRadius:'50%', flexShrink:0, background:av.bg, color:av.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:650 }}>
                    {initials(o.buyer_name)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:550, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={o.buyer_name || 'Unknown buyer'}>
                      {o.buyer_name || 'Unknown buyer'}
                    </div>
                    <div style={{ fontSize:11, color:'var(--text3)', fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      #{o.ebay_receipt_id} · {fmtDate(o.ebay_created_at)}
                      {o.shopify_order_name && <span style={{ color:'var(--sh)' }}> · {o.shopify_order_name}</span>}
                    </div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:650, color:'var(--green)', fontVariantNumeric:'tabular-nums', flexShrink:0 }}>
                    ${parseFloat(o.total_price||0).toFixed(2)}
                  </div>
                </div>
              )
            })}

            {showSync && recentActivity.map(a => <ActivityRow key={a.id} item={a} compact />)}

            {feedEmpty && (
              <div className="empty" style={{ padding:'28px 0' }}>
                <div className="empty-icon" aria-hidden="true">📋</div>
                <div style={{ fontSize:12 }}>
                  {showOrdersSection && feedTab === 'orders' ? t('no_orders_yet') : t('no_activity')}
                </div>
                {/* "Fetch orders" is the one action an empty feed offered, and
                    it opens Orders. Hidden with the section rather than left
                    pointing at a redirect. */}
                {showOrdersSection && feedTab !== 'sync' && (
                  <button className="btn btn-sm btn-primary" style={{ marginTop:10 }} onClick={() => go('/orders')}>
                    {t('fetch_orders')}
                  </button>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
